import { ChevronDown, ChevronUp, Copy, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { MaskIcon } from '@/components/ui/MaskIcon';
import { cn } from '@/lib/utils';

import type { BuilderField, BuilderSection } from '../builderModel';
import { addressSubfieldsOf } from '../elements/answers';
import type { FieldConfig } from '../elements/fieldConfig';
import { elementFor, isKnownElement } from '../elements/registry';
import { CascadeEditor } from './CascadeEditor';
import { FieldOptionsEditor } from './FieldOptionsEditor';

interface Props {
  sections: BuilderSection[];
  activeSectionKey: string;
  onSelectSection: (key: string) => void;
  onAddSection: () => void;
  onRemoveSection: (key: string) => void;
  onUpdateSection: (key: string, patch: Partial<BuilderSection>) => void;
  onUpdateField: (sectionKey: string, fieldKey: string, patch: Partial<BuilderField>) => void;
  onRemoveField: (sectionKey: string, fieldKey: string) => void;
  onDuplicateField: (sectionKey: string, fieldKey: string) => void;
}

const INPUT_CLASS =
  'min-h-[42px] w-full rounded-lg border border-placeholder bg-white px-4 py-2.5 text-[length:var(--text-14)] text-[#323338] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary focus:ring-1 focus:ring-primary/20';

/**
 * The middle column of the questionnaire builder — section tabs, the section heading card, and one
 * card per field.
 *
 * WHICH EDITORS A FIELD SHOWS IS DECIDED BY THE ELEMENT REGISTRY, not by conditions here. The
 * reference asks `field.type !== "single-choice" && field.type !== "multiple-choice" && …` inline
 * at each editor, which is why adding State / District / City to its palette gave them a
 * placeholder input and no options list: three new type names would have had to be added to five
 * separate conditions. Here each element declares its capabilities once and this panel reads them.
 */
export function QuestionnairesConfigPanel({
  sections,
  activeSectionKey,
  onSelectSection,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  onUpdateField,
  onRemoveField,
  onDuplicateField,
}: Props) {
  const activeSection = sections.find((s) => s.key === activeSectionKey) ?? sections[0];

  /**
   * Which cards are open, by key. The section heading card starts open; field cards start closed.
   *
   * An explicit map rather than "one card at a time": comparing two questions' settings means
   * having both open, and a builder that closes one card when you open another makes that
   * impossible.
   */
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, defaultOpen: boolean) => !(collapsed[key] ?? !defaultOpen);
  const toggle = (key: string, defaultOpen: boolean) =>
    setCollapsed((prev) => ({ ...prev, [key]: isOpen(key, defaultOpen) }));

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#F4F6FD]">
      {/* Section tabs */}
      <div className="hide-scrollbar relative z-10 flex shrink-0 items-end overflow-x-auto overflow-y-hidden bg-[#F4F6FB] pt-3 pr-4">
        {sections.map((section) => {
          const active = section.key === activeSectionKey;
          return (
            // The tab is a CONTAINER holding two sibling buttons — select and remove — rather than
            // a button with a second one nested inside it. Nesting is invalid HTML, and the usual
            // workaround (a span with role="button" and a stopPropagation guard) gives the remove
            // action no real keyboard behaviour. Two siblings both tab to, both fire on Enter.
            <div
              key={section.key}
              className={cn(
                'relative top-px flex shrink-0 items-center gap-2 whitespace-nowrap rounded-t-xl px-[clamp(12px,1vw,16px)] py-[clamp(8px,0.8vw,10px)] text-[length:var(--text-14)] transition-all',
                active
                  ? 'z-20 border border-[#CBD5E1] border-b-white bg-white font-semibold text-[#334155]'
                  : 'border border-transparent bg-[#E6EAF899] font-medium text-[#64748B] hover:bg-[#E2E8F0] hover:text-[#0F172A]',
              )}
            >
              <button
                type="button"
                onClick={() => onSelectSection(section.key)}
                className="cursor-pointer whitespace-nowrap"
              >
                {section.name}
              </button>

              {/*
                The close cross shows on EVERY tab, not only the active one. Ours used to require
                selecting a tab before it could be removed, which is two clicks to delete and — on
                the tab you are not looking at — no visible way to do it at all.
              */}
              {sections.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove ${section.name}`}
                  onClick={() => onRemoveSection(section.key)}
                  className="ml-1 shrink-0 cursor-pointer rounded-full bg-[#EBF2FF] p-0.5 text-primary transition-colors hover:bg-blue-100"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          );
        })}

        <span className="relative -top-px mx-0.5 h-6.5 w-px shrink-0 bg-[#B7BCCF]" />

        <button
          type="button"
          onClick={onAddSection}
          className="flex shrink-0 items-center gap-1 rounded-t-xl border border-transparent bg-[#E6EAF899] px-4 py-2.5 text-[length:var(--text-14)] font-medium text-[#64748B] transition-colors hover:bg-[#E2E8F0] hover:text-[#0F172A]"
        >
          <Plus size={13} /> Add Section
        </button>
      </div>

      {/* Cards */}
      <div className="custom-scrollbar flex h-0 min-h-0 flex-1 flex-col gap-[clamp(8px,0.7vw,10px)] overflow-y-auto bg-white px-[clamp(16px,1.2vw,20px)] py-[clamp(14px,1vw,18px)]">
        {activeSection && (
          <>
            {/* Section heading — what the APPLICANT reads. Separate from the tab name above. */}
            <Card
              open={isOpen('section-title', true)}
              onToggle={() => toggle('section-title', true)}
              heading={
                <>
                  <span className="shrink-0 text-[20px] font-medium leading-none text-[#4B5563]">
                    H
                  </span>
                  <h4 className="m-0 text-[length:var(--text-18)] font-medium text-[#041A3A]">
                    Section Title
                  </h4>
                </>
              }
            >
              <ClearableInput
                label="Title"
                value={activeSection.title}
                placeholder="Enter section title"
                onChange={(title) => onUpdateSection(activeSection.key, { title })}
              />
              <div className="mt-4">
                <ClearableInput
                  label="Description"
                  value={activeSection.description}
                  placeholder="Enter section description"
                  onChange={(description) => onUpdateSection(activeSection.key, { description })}
                />
              </div>
            </Card>

            {activeSection.fields.map((field) => {
              const element = elementFor(field.fieldType);
              const caps = element.capabilities;
              const cardKey = `field-${field.key}`;
              const open = isOpen(cardKey, false);

              const patchConfig = (next: FieldConfig) =>
                onUpdateField(activeSection.key, field.key, { config: next });

              return (
                <Card
                  key={field.key}
                  open={open}
                  onToggle={() => toggle(cardKey, false)}
                  heading={
                    <>
                      <MaskIcon
                        src={element.icon}
                        className="h-4.5 w-4.5 shrink-0 text-gray66"
                        label={element.label}
                      />
                      <h4 className="m-0 truncate text-[length:var(--text-18)] font-medium text-[#041A3A]">
                        {element.label}
                      </h4>
                      {/*
                        A field type this build's palette does not offer — an element from a newer
                        release, or one of the retired layout types. Flagged rather than hidden:
                        it is still saved and still shown to applicants, so an admin needs to know
                        it is there and that the editors below are generic.
                      */}
                      {!isKnownElement(field.fieldType) && (
                        <span className="shrink-0 rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[length:var(--text-12)] font-medium text-orangeDA">
                          Unsupported type
                        </span>
                      )}
                    </>
                  }
                  actions={
                    <>
                      <div className="flex select-none items-center gap-2.5">
                        <Toggle
                          checked={field.isMandatory}
                          label={`Mandatory: ${field.label || element.label}`}
                          onChange={(isMandatory) =>
                            onUpdateField(activeSection.key, field.key, { isMandatory })
                          }
                        />
                        <span className="text-[length:var(--text-14)] text-gray66">Mandatory</span>
                      </div>
                      <IconAction
                        label="Duplicate"
                        onClick={() => onDuplicateField(activeSection.key, field.key)}
                      >
                        <Copy size={15} />
                      </IconAction>
                      <IconAction
                        label="Delete"
                        danger
                        onClick={() => onRemoveField(activeSection.key, field.key)}
                      >
                        <Trash2 size={15} />
                      </IconAction>
                    </>
                  }
                >
                  <ClearableInput
                    label="Title"
                    value={field.label}
                    placeholder={element.titleHint}
                    onChange={(label) => onUpdateField(activeSection.key, field.key, { label })}
                  />

                  {caps.placeholder && (
                    <div className="mt-4">
                      <ClearableInput
                        label={element.placeholderLabel}
                        value={field.placeholder}
                        placeholder={
                          element.placeholderLabel === 'Consent Statement'
                            ? 'Ex. I agree to the terms and conditions'
                            : 'Enter placeholder'
                        }
                        onChange={(placeholder) =>
                          onUpdateField(activeSection.key, field.key, { placeholder })
                        }
                      />
                    </div>
                  )}

                  {caps.addressSubfields && (
                    <div className="mt-4 flex flex-col gap-1.5">
                      <span className="block text-[length:var(--text-14)] text-black">
                        Address Sub-Fields
                      </span>
                      <div className="flex flex-col gap-3 text-[length:var(--text-14)] text-[#475569]">
                        {addressSubfieldsOf(field.config).map((sub, index) => (
                          <div
                            key={sub.id}
                            className="flex flex-col rounded-lg border border-[#E2E8F0] bg-white p-3 shadow-2xs"
                          >
                            <div className="mb-2 flex items-center gap-2">
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                              <span className="text-[13.5px] font-semibold text-[#041A3A]">
                                {sub.title || `Line ${index + 1}`}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <SubfieldInput
                                label="Title"
                                value={sub.title}
                                onChange={(title) =>
                                  patchConfig({
                                    ...field.config,
                                    addressSubfields: addressSubfieldsOf(field.config).map((s) =>
                                      s.id === sub.id ? { ...s, title } : s,
                                    ),
                                  })
                                }
                              />
                              <SubfieldInput
                                label="Placeholder"
                                value={sub.placeholder}
                                onChange={(placeholder) =>
                                  patchConfig({
                                    ...field.config,
                                    addressSubfields: addressSubfieldsOf(field.config).map((s) =>
                                      s.id === sub.id ? { ...s, placeholder } : s,
                                    ),
                                  })
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/*
                    The flat options list is hidden once a cascade exists — a cascade's first tier
                    IS the option list, and showing both would present two editors for the same
                    dropdown with only one of them driving what the applicant sees.
                  */}
                  {caps.options && !(caps.cascade && field.config.cascade) && (
                    <div className="mt-4">
                      <FieldOptionsEditor
                        options={field.config.options ?? []}
                        onChange={(options) => patchConfig({ ...field.config, options })}
                        // Only for types that can hold dependent dropdowns. Supplying it is what
                        // lets a multi-column sheet turn this field into a cascade, so the single
                        // Import button covers both shapes and there is no second one to confuse
                        // it with.
                        onImportCascade={
                          caps.cascade
                            ? (cascade) => patchConfig({ ...field.config, cascade })
                            : undefined
                        }
                      />
                    </div>
                  )}

                  {caps.cascade && <CascadeEditor config={field.config} onChange={patchConfig} />}

                  {caps.validationMessage && (
                    <div className="mt-4">
                      <ClearableInput
                        label="Validation Error Message"
                        value={field.validationMessage}
                        placeholder="Ex. This field is required"
                        onChange={(validationMessage) =>
                          onUpdateField(activeSection.key, field.key, { validationMessage })
                        }
                      />
                    </div>
                  )}
                </Card>
              );
            })}

            {activeSection.fields.length === 0 && (
              <div className="mt-1 flex select-none items-center justify-center rounded-xl border-2 border-dashed border-[#E2E8F0] bg-[#F8FAFC] py-8 text-center text-[length:var(--text-14)] text-[#94A3B8]">
                Click an element from the left panel to add it here
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function Card({
  open,
  onToggle,
  heading,
  actions,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  heading: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="h-auto shrink-0 rounded-lg border border-[#CBD5E1] bg-white transition-all">
      <div className="flex select-none items-center justify-between px-5 py-4">
        {/*
          The heading is the toggle, so clicking anywhere along the card's title opens it. The
          actions sit OUTSIDE that button rather than inside with a stopPropagation guard —
          nesting a toggle inside a toggle is how you get a Delete that also collapses the card.
        */}
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          {heading}
        </button>
        <div className="flex shrink-0 items-center gap-2 text-[#9CA3AF]">
          {actions}
          <IconAction label={open ? 'Collapse' : 'Expand'} onClick={onToggle}>
            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </IconAction>
        </div>
      </div>

      {open && (
        <div className="flex flex-col px-[clamp(16px,1.2vw,20px)] pt-0 pb-[clamp(14px,1vw,18px)]">
          {children}
        </div>
      )}
    </div>
  );
}

function IconAction({
  label,
  danger,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 transition-colors',
        danger
          ? 'text-destructive hover:bg-red-50 hover:text-[#DC2626]'
          : 'text-[#9CA3AF] hover:bg-gray-100 hover:text-[#0F172A]',
      )}
    >
      {children}
      <span
        className={cn(
          'hidden text-[length:var(--text-14)] xl:inline',
          danger ? 'text-destructive' : 'text-gray66',
        )}
      >
        {label}
      </span>
    </button>
  );
}

function ClearableInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="block text-[length:var(--text-14)] text-black">{label}</span>
      <div className="relative flex w-full items-center">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn(INPUT_CLASS, 'pr-9')}
        />
        {value.length > 0 && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => onChange('')}
            className="absolute top-1/2 right-3 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#94A3B8] transition-colors hover:bg-gray-100 hover:text-[#475569]"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function SubfieldInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-black">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(INPUT_CLASS, 'min-h-0 py-1.5 text-[13px]')}
      />
    </label>
  );
}

function Toggle({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full transition-colors',
        checked ? 'bg-green00' : 'bg-[#D1D5DB]',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
          checked ? 'left-[18px]' : 'left-0.5',
        )}
      />
    </button>
  );
}
